import { Button as AntButton, ButtonProps as AntButtonProps } from 'antd'
import { ButtonProps, ButtonVariant, ButtonSize } from './Button.types'
import { colors } from '@/config/design-tokens'

/**
 * Button Component
 *
 * A flexible button component that wraps Ant Design's Button with our design system variants.
 *
 * @example
 * // Primary button
 * <Button variant="primary" onClick={handleClick}>
 *   Save Changes
 * </Button>
 *
 * @example
 * // Secondary button with icon
 * <Button variant="secondary" icon={<PlusOutlined />}>
 *   Add Item
 * </Button>
 *
 * @example
 * // Loading button
 * <Button variant="primary" loading>
 *   Processing...
 * </Button>
 */
export function Button({
  variant = 'primary',
  size = 'small',
  icon,
  iconPosition = 'start',
  loading = false,
  disabled = false,
  block = false,
  onClick,
  children,
  htmlType = 'button',
  className = '',
  danger = false,
}: ButtonProps) {
  /**
   * Map our design system variants to Ant Design button props
   */
  const getAntButtonProps = (): Partial<AntButtonProps> => {
    // Danger buttons always use primary type with danger flag
    if (danger || variant === 'danger') {
      return {
        type: 'primary',
        danger: true,
      }
    }

    switch (variant) {
      case 'primary':
        return { type: 'primary' }

      case 'secondary':
        return { type: 'default' }

      case 'tertiary':
        return {
          type: 'default',
          style: {
            borderColor: colors.neutral[300],
            backgroundColor: 'transparent',
            color: colors.neutral[950],
          },
        }

      case 'text':
        return { type: 'text' }

      default:
        return { type: 'default' }
    }
  }

  /**
   * Map our size to Ant Design size
   */
  const getAntSize = (): AntButtonProps['size'] => {
    switch (size) {
      case 'small':
        return 'small'
      case 'large':
        return 'large'
      case 'medium':
      default:
        return 'middle'
    }
  }

  const antButtonProps = getAntButtonProps()
  const antSize = getAntSize()

  // Extract style from antButtonProps for tertiary variant
  const { style: variantStyle, ...restAntButtonProps } = antButtonProps

  return (
    <AntButton
      size={antSize}
      icon={iconPosition === 'start' ? icon : undefined}
      loading={loading}
      disabled={disabled}
      block={block}
      onClick={onClick}
      htmlType={htmlType}
      className={className}
      style={variantStyle}
      {...restAntButtonProps}
    >
      {children}
      {iconPosition === 'end' && !loading && icon}
    </AntButton>
  )
}

// Export types
export type { ButtonProps, ButtonVariant, ButtonSize }
